import React from 'react';
import { useDispatch } from 'react-redux';

// Hooks
import { useDeepEqualSelector } from 'Shared/utils/hooks';

// Helpers
import { getAssignmentAttributes } from 'Shared/helpers';

// Actions
import { updateItem } from 'Shared/actions/todos';

// Selectors
import { getWorkspaceByUuid } from 'Shared/selectors/workspace';
import { getMeetingItemByUuid } from 'Shared/selectors/common';

// Components
import WpUserInput from 'Wpds/components/user-input/WpUserInput';
import { WpAvatar, WpAvatarEmpty } from 'Wpds/components/avatar';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './AssignedInput.scss';

type Props = {
  uuid?;
  avatarOnly?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

const AssigneeInput = ({ uuid, avatarOnly = false, disabled = false, tooltip }: Props) => {
  const dispatch = useDispatch();

  const todoData = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state));
  const { itemType, workspaceId } = todoData;

  const users = useDeepEqualSelector(state => getWorkspaceByUuid(workspaceId)(state)?.users || []);
  const assignmentAvatar = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignmentAvatar);
  const assignmentId = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignmentId);
  const assigneeUserId = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignee.userId);
  const assigneeName = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignee.personName);

  const handleAssignmentChange = assignedUser => {
    if (assigneeUserId === assignedUser?.id) return;

    dispatch(
      updateItem(uuid, {
        itemType,
        assignmentAttributes: getAssignmentAttributes({ assignmentId, assignedId: assignedUser?.personId }),
        assignmentPersonId: assignedUser?.personId,
        assignmentAvatar: assignedUser?.avatar,
        assignee: {
          personName: assignedUser?.fullName,
          userId: assignedUser?.id,
        },
      })
    );
  };

  const baseClassName = 'AssigneeInput';

  const AssignedComponent = () => {
    return assignmentAvatar ? (
      <div className={`${baseClassName}__owner`}>
        <div className={`${baseClassName}__owner__avatar`}>
          <WpAvatar avatar={assignmentAvatar} size="sm" />
        </div>
        {!avatarOnly && <span className={`${baseClassName}__owner__name`}>{assigneeName}</span>}
      </div>
    ) : (
      <div className={`${baseClassName}__owner`}>
        <div className={`${baseClassName}__unassigned-icon`}>
          <WpAvatarEmpty size="sm" />
        </div>
        {!avatarOnly && <span className={`${baseClassName}__owner__name--unassigned`}>Unassigned</span>}
      </div>
    );
  };

  const content = (
    <WpUserInput
      users={users}
      id="actionItemOwner"
      label=""
      disabled={disabled}
      removeUserLabel="Unassign"
      value={assigneeUserId}
      components={{
        innerDropdown: AssignedComponent,
      }}
      onChange={handleAssignmentChange}
      handleRemoveUser={handleAssignmentChange}
    />
  );

  if (tooltip) {
    return (
      <WpTooltip>
        <WpTooltipTrigger>
          <div>{content}</div>
        </WpTooltipTrigger>
        <WpTooltipContent>{tooltip}</WpTooltipContent>
      </WpTooltip>
    );
  }

  return <>{content}</>;
};

export default AssigneeInput;
